import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";

/**
 * Premium pill-style status badge for tracker Status column (Associated / Unassociated).
 * Theme-aware: green for Associated, refined muted for Unassociated; centered in cell.
 */
const greenFocusRing = "0 0 0 2px rgba(160, 196, 109, 0.4)";

export default function TrackerStatusPill({ isAssociated }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const label = isAssociated ? "Associated" : "Unassociated";
  const Icon = isAssociated ? InfoOutlinedIcon : LinkOffIcon;

  const pillStyles = isAssociated
    ? {
        backgroundColor: isDark ? theme.palette.success.dark : theme.palette.success.main,
        color: theme.palette.success.contrastText ?? "#fff",
        border: isDark
          ? "1px solid rgba(160, 196, 109, 0.35)"
          : "1px solid rgba(122, 156, 74, 0.45)",
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.25), 0 0 0 1px rgba(160, 196, 109, 0.08)"
          : "0 1px 3px rgba(122, 156, 74, 0.2), 0 0 0 1px rgba(122, 156, 74, 0.06)",
      }
    : {
        backgroundColor: isDark
          ? "hsl(215, 18%, 26%)"
          : "rgba(100, 116, 139, 0.12)",
        color: isDark ? "rgba(255,255,255,0.88)" : theme.palette.text.secondary,
        border: isDark
          ? "1px solid rgba(148, 163, 184, 0.2)"
          : "1px solid rgba(100, 116, 139, 0.2)",
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.15)"
          : "0 1px 2px rgba(0,0,0,0.04)",
      };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          alignSelf: "center",
          gap: { xs: "4px", sm: "6px" },
          minWidth: { xs: "8.5em", sm: "10.5em" },
          paddingLeft: { xs: "6px", sm: "10px" },
          paddingRight: { xs: "6px", sm: "10px" },
          paddingTop: { xs: "3px", sm: "4px" },
          paddingBottom: { xs: "3px", sm: "4px" },
          borderRadius: "9999px",
          fontSize: { xs: "0.6875rem", sm: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.025em",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
          ...pillStyles,
          "&:focus-visible": {
            outline: "none",
            boxShadow: greenFocusRing,
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
          "& .MuiSvgIcon-root": {
            fontSize: { xs: "0.8125rem", sm: "0.9375rem" },
            opacity: 1,
            margin: 0,
            flexShrink: 0,
            color: "inherit",
          },
        }}
      >
        <Icon fontSize="inherit" />
        {label}
      </Box>
    </Box>
  );
}
